import { REQUEST_TYPE_PRICE_UPDATE } from '@components/bid_request/RequestConstants';
import { shouldShowDiscount } from '@utils/discountLogic';
import { getAdderColumns } from '@utils/line-items/columnHelpers';
import type { EventSettableColumnId } from '@features/event-level-column-rename/types';
import type { EventRequest } from 'types';

/** Whether a column can be renamed on this event; when not, the i18n key naming the setting to turn on. */
export interface ColumnEnablement {
  disabledFieldTooltipKey?: string;
  enabled: boolean;
}

type EventSettingGate = (_request: EventRequest) => boolean;

interface EventSettingGateEntry {
  disabledFieldTooltipKey: string;
  isEnabledOnEvent: EventSettingGate;
}

const DISABLED_FIELD_TOOLTIP_KEY_PREFIX = 'bid_request.event_config_menu.column_labels.disabled_field_tooltips';

const disabledFieldTooltipKeyFor = (setting: string): string => `${DISABLED_FIELD_TOOLTIP_KEY_PREFIX}.${setting}`;

const showsPricingColumns: EventSettingGate = request => Boolean(request.ui_config?.lots?.show_pricing_columns);

const collectsItemsPerUnit: EventSettingGate = request => (
  showsPricingColumns(request) && Boolean(request.ui_config?.lots?.collect_items_per_unit)
);

const showsDiscount: EventSettingGate = request => (
  showsPricingColumns(request)
    && shouldShowDiscount(request.ui_config?.lots, Boolean(request.company_show_discount))
);

// Suggested Price is live without the suggested-price-enhancement phase flag, so this eventSettingGate reads
// the event's own settings only.
const showsSuggestedPrice: EventSettingGate = request => (
  showsPricingColumns(request)
    && !request.anchor_pricing_disabled
    && Boolean(request.ui_config?.lots?.show_suggested_price_column)
    && request.request_type !== REQUEST_TYPE_PRICE_UPDATE
);

const allowsSupplierQuantityOverride: EventSettingGate = request => Boolean(request.supplier_quantity_enabled);

const eventHasAdderColumn: EventSettingGate = request => {
  const customColumnHeaders = (request.lots ?? []).flatMap(lot => lot.custom_column_headers ?? []);

  return showsPricingColumns(request)
    && getAdderColumns(customColumnHeaders).length > 0;
};

/**
 * The single mapping of column to the event setting it depends on. A column absent from this
 * table is always renameable; add new feature-dependent columns here, not in the drawer. Adder
 * columns count every adder header (like the buyer-side Line Item Settings drawer), not only
 * supplier-visible ones.
 */
const EVENT_SETTING_GATE_BY_CANONICAL_ID: Partial<Record<EventSettableColumnId, EventSettingGateEntry>> = {
  discountPerUnit: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('show_discount_column'), isEnabledOnEvent: showsDiscount },
  itemsPerUnit: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('collect_items_per_unit'), isEnabledOnEvent: collectsItemsPerUnit },
  lineItemPrice: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('collect_pricing'), isEnabledOnEvent: showsPricingColumns },
  pricePerUnit: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('collect_pricing'), isEnabledOnEvent: showsPricingColumns },
  quantityOfItems: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('collect_items_per_unit'), isEnabledOnEvent: collectsItemsPerUnit },
  quotedQuantityOfUnits: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('supplier_quantity_override'), isEnabledOnEvent: allowsSupplierQuantityOverride },
  suggestedPricePerUnit: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('use_suggested_pricing'), isEnabledOnEvent: showsSuggestedPrice },
  totalAdditionalCosts: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('adder_column'), isEnabledOnEvent: eventHasAdderColumn },
  totalAllInPrice: { disabledFieldTooltipKey: disabledFieldTooltipKeyFor('adder_column'), isEnabledOnEvent: eventHasAdderColumn },
};

/**
 * Decides, from the live event payload, whether the Rename Columns drawer lets a buyer edit a
 * column's name. A disabled column keeps whatever label is stored for it; this only governs
 * whether the drawer offers to change it.
 */
export const isColumnEnabledForEvent = (
  request: EventRequest,
  columnId: EventSettableColumnId,
): ColumnEnablement => {
  const eventSettingGate = EVENT_SETTING_GATE_BY_CANONICAL_ID[columnId];

  if (!eventSettingGate || eventSettingGate.isEnabledOnEvent(request)) return { enabled: true };

  return { disabledFieldTooltipKey: eventSettingGate.disabledFieldTooltipKey, enabled: false };
};
